import * as _ from 'underscore'

import {
    ConfigHelper,
    ConvertHelper,
    Dictionary,
    DataProvider,
    KeyType,
    KeyValuePair,
    TypeHelper
} from './'

import { Enums } from '../models/common/enums'
import { Map, MapOptions, MapLegendItem } from '../modules/common/map2/map.directive.controller'
import { ObjectCustomString } from '../models/util/object-custom-string'
import { IReferenceType } from '../models/util/interfaces'
import { OpCoord, OpMarker, OpRange } from '../models/custom/op-map-area'
import { OpLocation } from '../models/core/op-location'
import { OpView, GetViewColumnOptions } from '../models/core/op-view'

// TODO zastąpić metodami z DP
import { OperatorLayoutSettings } from './operator-layout-settings'
import { OpViewColumn } from '../models/core/op-view-column';
import { OpOperator, OpProfile, OpProfileData, OpDataType } from '../models/core'

// Private Types

type OpLocationToOpMarkerOptions = {
    view?: OpView

    onClick?: (marker: OpMarker) => void
}

// Private Types End

export class MapHelper {

    // Consts

    public static get LatitudeFieldName(): string { return 'latitude' }
    public static get LongitudeFieldName(): string { return 'longitude' }
    public static get MarkerStringFieldName(): string { return 'markerString' }
    public static get MarkerColorFieldName(): string { return 'markerColorReference' }

    // Consts End

    private static get LOG_STRING(): string { return '[SIMAX.Helpers.MapHelper] - %s' }

    // Methods

    /**
     * TODO Przenieść logikę tworzenia filtrów do grida
     */
    public static getCoordColumnNames(dataProvider: DataProvider, refereneceType: Enums.ReferenceType = Enums.ReferenceType.IdLocation): JQueryPromise<[string, string]> {
        let latColName: string = 'LATITUDE COLUMN'
        let lonColName: string = 'LONGITUDE COLUMN'

        return dataProvider.View
            .getActiveView(refereneceType)
            .then((view: OpView) => {
                if (view != null) {
                    const getViewColumnParameters = new GetViewColumnOptions(Enums.DataType.LOCATION_LATITUDE)

                    getViewColumnParameters.viewColumnType = Enums.ViewColumnType.Standard

                    const latCol = view.getViewColumn(getViewColumnParameters)
                    if (latCol) latColName = latCol.name

                    getViewColumnParameters.idDataType = Enums.DataType.LOCATION_LONGITUDE
                    const lonCol = view.getViewColumn(getViewColumnParameters)
                    if (lonCol) lonColName = lonCol.name
                }

                return [latColName, lonColName] as [string, string]
            })

    }

    private static getInitParams(loggedOperator: OpOperator, dataProvider: DataProvider): JQueryPromise<Dictionary<any>> {
        if (
            loggedOperator != null
            && loggedOperator.profile != null
            && loggedOperator.profile.parameters != null
            && dataProvider != null
        ) {
            return OpProfile.loadNavigationProperties(dataProvider, [loggedOperator.profile])
                .then((profiles: OpProfile[]) => {
                    const filteredProfileParameters = _
                        .chain(_.first(profiles).parameters.toArray())
                        .filter((paramater: OpProfileData) => {
                            if (paramater.idDataType == Enums.SimaxOperatorSetting.ProfileMapProviderParamMapId ||
                                paramater.idDataType == Enums.SimaxOperatorSetting.ProfileMapProviderParamOwner ||
                                paramater.idDataType == Enums.SimaxOperatorSetting.ProfileMapProviderParamUrl) {
                                return true
                            }

                            return false
                        })
                        .map((parameter: OpProfileData) => {
                            return new KeyValuePair(parameter.idDataType, parameter.value)
                        })
                        .value()

                    return new Dictionary(filteredProfileParameters, KeyType.String)
                })
        }
        return null
    }

    public static getDefaultOptions(dataProvider: DataProvider): JQueryPromise<MapOptions> {
        const promisesList: JQueryPromise<any>[] = []

        return dataProvider
            .Operator
            .getLoggedOperator()
            .then((loggedOperator: OpOperator) => {
                const operatorSettingsPromise: JQueryPromise<any[]> = dataProvider
                    .Operator
                    .getOperatorSettings(dataProvider.Operator.getLastIdOperator(), [
                        Enums.SimaxOperatorSetting.MapZoomDefaultGoogle,
                        Enums.SimaxOperatorSetting.MapProvider,
                        Enums.SimaxOperatorSetting.ProfileMapProviderParamMapId,
                        Enums.SimaxOperatorSetting.ProfileMapProviderParamOwner,
                        Enums.SimaxOperatorSetting.ProfileMapProviderParamUrl])
                const initParamsPromise = this.getInitParams(loggedOperator, dataProvider)
                promisesList.push(operatorSettingsPromise, initParamsPromise)

                return $
                    .when(
                        ...promisesList
                    )
                    .then((operatorSettings: any[], initParams: Dictionary<any>) => {
                        let initZoom: number = null
                        let provider: Enums.MapProvider = null

                        if (operatorSettings != null && operatorSettings[0] != null) {
                            initZoom = operatorSettings[0]
                        }

                        if (operatorSettings != null && operatorSettings[1] != null) {
                            provider = operatorSettings[1]
                        }

                        if (initZoom == null) {
                            initZoom = ConfigHelper.getValue(Enums.SimaxConfigSection.Map, ConfigHelper.Key.InitZoom)
                            console.info(MapHelper.LOG_STRING, "No GOOGLE_MAP_ZOOM_DEFAULT data type in operator settings. Trying to read init zoom from config.")
                        }

                        if (initZoom == null) {
                            initZoom = 1

                            console.warn(MapHelper.LOG_STRING, "No init zoom key in config. Using value '1'.")
                        }

                        let clusterZoom: number = ConfigHelper.getValue(Enums.SimaxConfigSection.Map, ConfigHelper.Key.ClusterZoom)

                        if (clusterZoom == null) {
                            clusterZoom = 14

                            console.warn(MapHelper.LOG_STRING, "No cluster zoom key in config. Using value '14'.")
                        }

                        let maxZoom: number = ConfigHelper.getValue(Enums.SimaxConfigSection.Map, ConfigHelper.Key.MaxZoom)

                        if (maxZoom == null) {
                            maxZoom = 15

                            console.warn(MapHelper.LOG_STRING, "No max zoom key in config. Using value '15'.")
                        }

                        if (provider == null) {
                            provider = ConvertHelper.parseEnum<Enums.MapProvider>(Enums.MapProvider, ConfigHelper.getValue(Enums.SimaxConfigSection.Map, ConfigHelper.Key.Map_Provider))

                            console.warn(MapHelper.LOG_STRING, `Using provider from config. Using value ${(provider != null ? provider.toString() : "null")}`)
                        }

                        if (provider == null) {
                            provider = Enums.MapProvider.GoogleMap
                            console.warn(MapHelper.LOG_STRING, `No provider key in config. Using value ${provider}`)
                        }

                        const initCenter: OpCoord = new OpCoord()

                        let mapCenter: any = ConfigHelper.getValue(Enums.SimaxConfigSection.Map, ConfigHelper.Key.Center)

                        if (mapCenter == null) {
                            initCenter.latitude = 0
                            initCenter.longitude = 0

                            console.warn(MapHelper.LOG_STRING, "No center key in config. Using [0, 0] coordinates.")
                        } else {
                            initCenter.latitude = mapCenter[0]
                            initCenter.longitude = mapCenter[1]
                        }

                        return {
                            hideTooltip: false,
                            hideCounter: false,
                            hideCloseButton: false,

                            isClustering: OperatorLayoutSettings.getSetting('isMapClustering', true), // TODO, zamienić
                            isLegendVisible: OperatorLayoutSettings.getSetting('isMapLegendShown', false),

                            initZoom,
                            initCenter,
                            clusterZoom,
                            maxZoom,
                            provider,
                            initParams,

                            defaultMarkerIconOptions: this.getDefaultMarkerIcon(),

                            onMapInit: (map: Map) => {
                                console.warn(MapHelper.LOG_STRING, "No onMapInit methods set.")
                            },

                            onClusteringChange: isClustering => {
                                OperatorLayoutSettings.setSetting('isMapClustering', isClustering)
                            },

                            onLegendVisiblityChange: isLegendVisible => {
                                OperatorLayoutSettings.setSetting('isMapLegendShown', isLegendVisible)
                            },
                        }
                    })
            })
    }

    public static getDefaultMarkerIcon(): any {
        let iconConfig: any = ConfigHelper.getValue(Enums.SimaxConfigSection.Map, ConfigHelper.Key.DefaultMarkerIcon)

        if (iconConfig == null) {
            iconConfig = {
                path: 'm -4.1665e-4,-6.8424763 c -1.10310295,0 -1.96427205,0.8700175 -1.96427205,2.0453532 0,0.6916158 0.3733412,1.2710306 0.7891039,1.7358114 C -0.59956246,-2.4173769 -0.2994351,-2.0477844 -4.1665e-4,0 0.29860178,-2.0477844 0.59872919,-2.4173769 1.1747515,-3.0613117 c 0.4157627,-0.4647808 0.7891039,-1.0441956 0.7891039,-1.7358114 0,-1.1753357 -0.8611691,-2.0453532 -1.96427205,-2.0453532 z M 0.30149533,-4.8451824 A 0.30191204,0.30191204 0 0 1 -4.1671109e-4,-4.5432704 0.30191204,0.30191204 0 0 1 -0.30232875,-4.8451824 0.30191204,0.30191204 0 0 1 -4.1671109e-4,-5.1470945 0.30191204,0.30191204 0 0 1 0.30149533,-4.8451824 Z',
                fillColor: '#FE7569',
                fillOpacity: 1,
                scale: 3,
                strokeColor: '#000000',
                strokeWeight: 1.1,
            }

            console.warn(MapHelper.LOG_STRING, 'No default marker icon in config. Using a hardcoded one.')
        }

        return iconConfig
    }

    /**
     * @deprecated Use MapService#createMarker
     */
    public static opLocationToOpMarker(location: OpLocation, options: OpLocationToOpMarkerOptions = {}): OpMarker {
        if (location != null) {
            const marker: OpMarker = new OpMarker()

            marker.referenceValue = location.idLocation


            // Coords
            let latitude: number = location.latitude
            let longitude: number = location.longitude

            if ((latitude == null || longitude == null) && options.view != null) {
                const latitudeColumn: OpViewColumn = options.view.getViewColumn(new GetViewColumnOptions(Enums.DataType.LOCATION_LATITUDE))
                const longitudeColumn: OpViewColumn = options.view.getViewColumn(new GetViewColumnOptions(Enums.DataType.LOCATION_LONGITUDE))

                if (latitudeColumn != null && longitudeColumn != null) {
                    const latitudeDynamicValue = _.find(location.dynamicValues, dynamicValue => dynamicValue.Key.toLowerCase() === latitudeColumn.name.toLowerCase())
                    const longitudeDynamicValue = _.find(location.dynamicValues, dynamicValue => dynamicValue.Key.toLowerCase() === longitudeColumn.name.toLowerCase())

                    if (latitudeDynamicValue != null && longitudeDynamicValue != null) {
                        latitude = latitudeDynamicValue.Value
                        longitude = longitudeDynamicValue.Value
                    }
                }
            }

            if (latitude == null || longitude == null) {
                return null
            }

            marker.coords = new OpCoord()
            marker.coords.latitude = latitude
            marker.coords.longitude = longitude


            // Tooltip
            const customFormatString: string = ObjectCustomString.getCutomMarkerStringFormat(location)

            if (customFormatString != null) {
                marker.tooltip = ObjectCustomString.getObjectCustomString(customFormatString, location)
            }
            else {
                marker.tooltip = location.toString()

                if (TypeHelper.isNullOrEmpty(marker.tooltip)) {
                    marker.tooltip = location.idLocation.toString()
                }
            }


            // Color
            const colorReferenceValue: Enums.KnownColorsEnum = location.dataList.getValue(Enums.DataType.HELPER_COLOR)

            if (colorReferenceValue != null) {
                marker.fillColor = ConvertHelper.knownColorEnumToHexColor(colorReferenceValue)
            }


            // Options
            if (options != null) {

                if (_.isFunction(options.onClick)) {
                    marker.onClick = () => {
                        options.onClick(marker)
                    }
                }
            }

            return marker
        }

        return null
    }

    public static getLegendItems(dataProvider: DataProvider, referenceId: number): JQueryPromise<MapLegendItem[]> {
        return dataProvider
            .Operator
            .getOperatorSettings(dataProvider.Operator.getLastIdOperator(), [Enums.SimaxOperatorSetting.MarkerColor])
            .then((result: any[]) => {
                let legendItems: MapLegendItem[] = []

                if (result && result[0]) {
                    legendItems = result[0].filter((item: any) => item.ReferenceType == referenceId).map((item: any) => <MapLegendItem>{
                        color: item.Color,
                        description: item.Description || '',
                        icon: null,
                        iconId: null
                    })
                }

                return legendItems
            })
    }

    public static setCustomApperiance(mapRanges: OpRange[]): OpRange[] {
        return _.map(mapRanges, ((range: OpRange) => {
            const preparedRange: OpRange = new OpRange(range)

            const rangeStyle: RangeStyle = RangeStyle.getRangeStyle(0.8, 2, 0.35, 1000)

            preparedRange.lineColor = range.lineColor
            preparedRange.fillColor = range.fillColor
            preparedRange.selectedLineColor = range.selectedLineColor

            preparedRange.lineOpacity = rangeStyle.lineOpacity
            preparedRange.lineWeight = rangeStyle.lineWeight
            preparedRange.fillOpacity = rangeStyle.fillOpacity
            preparedRange.radius *= rangeStyle.radius

            return preparedRange
        })
        )
    }

    // Methods End
}

class RangeStyle {

    public static getRangeStyle(lineOpacity: number, lineWeight: number, fillOpacity: number, radius: number): RangeStyle {
        const rangeStyle: RangeStyle = new RangeStyle()
        rangeStyle.lineOpacity = lineOpacity
        rangeStyle.lineWeight = lineWeight
        rangeStyle.fillOpacity = fillOpacity
        rangeStyle.radius = radius
        return rangeStyle
    }

    public lineOpacity: number
    public lineWeight: number
    public fillOpacity: number
    public radius: number

    constructor() { }

}